import { defineStore } from "pinia";
import authService from "../services/auth.service.ts";
import type { ParsedUserAgent } from "../utils/userAgent";

export interface AgenticAccess {
  id: string;
  url?: string;
  name: string;
  ip?: string;
  // i18n key set when the redeemed device's IP can't be granted (e.g. loopback);
  // the panel shows this reason and blocks Approve.
  ipError?: string;
  userAgent?: string;
  parsedUserAgent?: ParsedUserAgent | null;
  status: "pending_link" | "pending_approval" | "approved" | "revoked";
  durationSeconds: number;
  aclIDs: string[];
  createdAt: string;
  linkExpiresAt: string;
  redeemedAt?: string;
  approvedAt?: string;
  expiresAt?: string;
  // Worst-case live grant status code across the agent's knocs (AclStatusKey);
  // approved agents only. Absent when the approval produced no grant rows.
  grantStatus?: number;
  // Per-knoc breakdown behind grantStatus.
  aclStatuses?: { aclID: string; status: number }[];
}

export const useAgenticAccessStore = defineStore("agenticAccess", {
  state: () => ({
    agents: [] as AgenticAccess[],
    loaded: false,
    loading: false,
  }),

  actions: {
    async fetchAgents() {
      if (this.loading) return;
      this.loading = true;
      try {
        this.agents = await authService.listAgenticAccesses();
        this.loaded = true;
      } catch {
        this.agents = [];
        this.loaded = true;
      } finally {
        this.loading = false;
      }
    },

    async createAgent(data: { name: string; durationSeconds: number; aclIDs: string[] }): Promise<AgenticAccess> {
      const agent = await authService.createAgenticAccess(data);
      // A concurrent poll may have already inserted this agent; replace in place if so.
      const idx = this.agents.findIndex((a) => a.id === agent.id);
      if (idx >= 0) {
        this.agents[idx] = agent;
      } else {
        this.agents.unshift(agent);
      }
      return agent;
    },

    async approveAgent(eaId: string, expectedIP: string) {
      await authService.approveAgenticAccess(eaId, expectedIP);
      await this.fetchAgents();
    },

    async revokeAgent(eaId: string) {
      await authService.revokeAgenticAccess(eaId);
      const agent = this.agents.find((a) => a.id === eaId);
      if (agent) agent.status = "revoked";
    },
  },
});
